import { DecimalPipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import locale from '@angular/common/locales/da';
import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
} from '@azure/msal-angular';
import { PublicClientApplication } from '@azure/msal-browser';
import * as Sentry from '@sentry/angular';
import { da } from 'date-fns/locale';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzCommentModule } from 'ng-zorro-antd/comment';
import { NzConfig, NZ_CONFIG } from 'ng-zorro-antd/core/config';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { en_US, NZ_DATE_LOCALE, NZ_I18N } from 'ng-zorro-antd/i18n';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzLayoutModule } from 'ng-zorro-antd/layout';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzResultModule } from 'ng-zorro-antd/result';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzStatisticModule } from 'ng-zorro-antd/statistic';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { FooterComponent } from 'src/app/components/footer/footer.component';
import { DesignLookupSearchFieldComponent } from 'src/app/components/header/design-lookup-search-field/design-lookup-search-field.component';
import { EquipmentSelectComponent } from 'src/app/components/header/equipment-select/equipment-select.component';
import { HeaderComponent } from 'src/app/components/header/header.component';
import { ProfileDropdownComponent } from 'src/app/components/header/profile-dropdown/profile-dropdown.component';
import { ValidationItemComponent } from 'src/app/components/header/validation/validation-summary-modal/validation-item/validation-item.component';
import { ValidationSummaryModalComponent } from 'src/app/components/header/validation/validation-summary-modal/validation-summary-modal.component';
import { ValidationComponent } from 'src/app/components/header/validation/validation.component';
import { ManageAdminsComponent } from 'src/app/components/main-content/admin/manage-admins/manage-admins.component';
import { CreateQuickUrlDrawerComponent } from 'src/app/components/main-content/admin/manage-quick-urls/create-quick-url-drawer/create-quick-url-drawer.component';
import { ManageQuickUrlsComponent } from 'src/app/components/main-content/admin/manage-quick-urls/manage-quick-urls.component';
import { ConstructionLogComponent } from 'src/app/components/main-content/design-lookup/construction-log/construction-log.component';
import { AddOrEditResponsibleUserDrawerComponent } from 'src/app/components/main-content/design-lookup/design-data/add-or-edit-responsible-user/add-or-edit-responsible-user-drawer.component';
import { DesignLookupComponent } from 'src/app/components/main-content/design-lookup/design-lookup.component';
import { ElementStatusComponent } from 'src/app/components/main-content/design-lookup/element-status/element-status.component';
import { MouldDecisionToolComponent } from 'src/app/components/main-content/design-lookup/equipment/mould-decision-tool/mould-decision-tool.component';
import { ImageSpinnerComponent } from 'src/app/components/main-content/design-lookup/image-spinner/image-spinner.component';
import { CommentListComponent } from 'src/app/components/main-content/design-lookup/lists/comment-list/comment-list.component';
import { ProcessingHistoryDocumentLinkComponent } from 'src/app/components/main-content/design-lookup/processing-history-document-link/processing-history-document-link.component';
import { WorkcenterSelectorComponent } from 'src/app/components/main-content/design-lookup/selectors/workcenter-selector/workcenter-selector.component';
import { DevelopmentNoveltyTableEditDrawer } from 'src/app/components/main-content/design-lookup/tables/development-novelty-table/development-novelty-table-edit/development-novelty-table-edit.drawer';
import { DevelopmentNoveltyTableComponent } from 'src/app/components/main-content/design-lookup/tables/development-novelty-table/development-novelty-table.component';
import { CharacteristicsTableComponent } from 'src/app/components/main-content/design-lookup/tables/specification-table/operations-table/characteristics-table/characteristics-table.component';
import { OperationsTableComponent } from 'src/app/components/main-content/design-lookup/tables/specification-table/operations-table/operations-table.component';
import { ItemStructureComponent } from 'src/app/components/main-content/design-lookup/teamcenter/item-structure/item-structure.component';
import { PartDesignRevisionStructureComponent } from 'src/app/components/main-content/design-lookup/teamcenter/part-design-revision-structure/part-design-revision-structure.component';
import { TechnicalSpecificationComponent } from 'src/app/components/main-content/design-lookup/technical-specification/technical-specification.component';
import { ToySafetyReportComponent } from 'src/app/components/main-content/design-lookup/toy-safety-report/toy-safety-report.component';
import { LoginFailedComponent } from 'src/app/components/main-content/errors/login-failed/login-failed.component';
import { NotFoundComponent } from 'src/app/components/main-content/errors/not-found/not-found.component';
import { UnauthorizedComponent } from 'src/app/components/main-content/errors/unauthorised/unauthorized.component';
import { RoleSelectionComponent } from 'src/app/components/main-content/role-selection/role-selection.component';
import { WarningIconComponent } from 'src/app/components/main-content/warning-icon/warning-icon.component';
import { WorklistPageComponent } from 'src/app/components/main-content/worklist-page/worklist-page.component';
import { WorklistComponent } from 'src/app/components/main-content/worklist-page/worklist/worklist.component';
import { SideMenuComponent } from 'src/app/components/side-menu/side-menu.component';
import { VisibleForRolesDirective } from 'src/app/directives/visible-for-roles.directive';
import { icons } from 'src/app/icons';
import { LegoUtilsModule, SapApiModule } from 'src/app/modules';
import {
  ArrayIsNotEmptyPipe,
  ContainsPipe,
  JoinDeepPipe,
  RoleLinksPipe,
  SortPipe,
  TrimZeroesPipe,
} from 'src/app/pipes';
import { environment } from 'src/environments/environment';
import {
  Configuration as EquipmentApiConfiguration,
  EquipmentApiModule,
} from 'src/generated-clients/equipment';
import {
  Configuration as MouldingProcessApiConfiguration,
  MouldingProcessApiModule,
} from 'src/generated-clients/moulding_process';
import {
  Configuration as MouldDesignApiConfiguration,
  MouldDesignApiModule,
} from 'src/generated-clients/mould_design';
import {
  Configuration as MouldLogApiConfiguration,
  MouldLogApiModule,
} from 'src/generated-clients/mould_log';
import {
  Configuration as ToySafetyReportsApiConfiguration,
  ToySafetyReportsApiModule,
} from 'src/generated-clients/toy-safety-reports';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AdminButtonComponent } from './components/main-content/admin/admin-button/admin-button.component';
import { CreateAdminDrawerComponent } from './components/main-content/admin/manage-admins/create-admin-drawer/create-admin-drawer.component';
import { EditQuickUrlDrawerComponent } from './components/main-content/admin/manage-quick-urls/edit-quick-url-drawer/edit-quick-url-drawer.component';
import { ConnectorComponent } from './components/main-content/design-lookup/connector/connector.component';
import { ControlSpecificationComponent } from './components/main-content/design-lookup/control-specification/control-specification.component';
import { DesignDataComponent } from './components/main-content/design-lookup/design-data/design-data.component';
import { ElementHistoryDocumentLinkComponent } from './components/main-content/design-lookup/element-log/element-history-document-link/element-history-document-link.component';
import { ElementHistoryLogComponent } from './components/main-content/design-lookup/element-log/element-history-log/element-history-log.component';
import { ElementLogComponent } from './components/main-content/design-lookup/element-log/element-log.component';
import { SiriusElementHistoryDocumentLinkComponent } from './components/main-content/design-lookup/element-log/sirius-element-history-document-link/sirius-element-history-document-link.component';
import { ElementRiskAssessmentComponent } from './components/main-content/design-lookup/element-risk-assessment/element-risk-assessment.component';
import { ConstructionDetailsComponent } from './components/main-content/design-lookup/equipment/construction-detail/construction-details.component';
import { EquipmentDetailsComponent } from './components/main-content/design-lookup/equipment/equipment-details/equipment-details.component';
import { EquipmentComponent } from './components/main-content/design-lookup/equipment/equipment.component';
import { MouldCardDetailsComponent } from './components/main-content/design-lookup/equipment/mould-card-details/mould-card-details.component';
import { MouldDevelopersComponent } from './components/main-content/design-lookup/equipment/mould-developers/mould-developers.component';
import { VisitationComponent } from './components/main-content/design-lookup/equipment/visitation/visitation.component';
import { GeneralInformationComponent } from './components/main-content/design-lookup/general-information/general-information.component';
import { InfoCodesComponent } from './components/main-content/design-lookup/general-information/info-codes/info-codes.component';
import { ItemLogComponent } from './components/main-content/design-lookup/general-information/item-log/item-log.component';
import { CostDetailsComponent } from './components/main-content/design-lookup/maintenance-management-system-details/cost-details/cost-details.component';
import { HistoryDetailsComponent } from './components/main-content/design-lookup/maintenance-management-system-details/history-details/history-details.component';
import { MaintenanceManagementSystemDetailsComponent } from './components/main-content/design-lookup/maintenance-management-system-details/maintenance-management-system-details.component';
import { MouldTechnicalSpecificationComponent } from './components/main-content/design-lookup/maintenance-management-system-details/mould-technical-specification/mould-technical-specification.component';
import { CreateEditMaterialBomDrawer } from './components/main-content/design-lookup/material-bom/create-edit-material-bom/create-edit-material-bom.drawer';
import { MaterialBomComponent } from './components/main-content/design-lookup/material-bom/material-bom.component';
import { MouldLogOverviewModalComponent } from './components/main-content/design-lookup/mould-log/mould-log-overview/mould-log-overview-modal/mould-log-overview-modal.component';
import { MouldLogOverviewComponent } from './components/main-content/design-lookup/mould-log/mould-log-overview/mould-log-overview.component';
import { MouldLogComponent } from './components/main-content/design-lookup/mould-log/mould-log.component';
import { PlanningReportComponent } from './components/main-content/design-lookup/planning-report/planning-report.component';
import { SharePointFileLinkCardComponent } from './components/main-content/design-lookup/share-point-file-link-card/share-point-file-link-card.component';
import { DevelopmentMilestonesFrontendTableComponent } from './components/main-content/design-lookup/tables/development-milestones-frontend-table/development-milestones-frontend-table.component';
import { EcoTableComponent } from './components/main-content/design-lookup/tables/eco-table/eco-table.component';
import { InfoCodesTableComponent } from './components/main-content/design-lookup/tables/info-codes-table/info-codes-table.component';
import { ItemLogsTableComponent } from './components/main-content/design-lookup/tables/item-logs-table/item-logs-table.component';
import { ItemRevisionDatasetTableComponent } from './components/main-content/design-lookup/tables/item-revision-dataset-table/item-revision-dataset-table.component';
import { ColourBrickComponent } from './components/main-content/design-lookup/tables/material-colour-table/colour-brick/colour-brick.component';
import { MaterialColourTableComponent } from './components/main-content/design-lookup/tables/material-colour-table/material-colour-table.component';
import { MaterialWhereUsedTableComponent } from './components/main-content/design-lookup/tables/material-where-used-table/material-where-used-table.component';
import { CharacteristicLongTextDialogComponent } from './components/main-content/design-lookup/tables/specification-table/operations-table/characteristics-table/characteristic-long-text-dialog/characteristic-long-text-dialog.component';
import { DatasetCardListComponent } from './components/main-content/design-lookup/teamcenter/dataset-card-list/dataset-card-list.component';
import { EDRevisionStructureComponent } from './components/main-content/design-lookup/teamcenter/ed-revision-structure/ed-revision-structure.component';
import { RelatedSpecificationStructureComponent } from './components/main-content/design-lookup/teamcenter/related-specification-structure/related-specification-structure.component';
import { TeamcenterTabHeaderComponent } from './components/main-content/design-lookup/teamcenter/teamcenter-tab-header/teamcenter-tab-header.component';
import { TeamcenterComponent } from './components/main-content/design-lookup/teamcenter/teamcenter.component';
import { TechnicalReviewComponent } from './components/main-content/design-lookup/technical-review/technical-review.component';
import { UnitsOfMeasureComponent } from './components/main-content/design-lookup/units-of-measure/units-of-measure.component';
import { DisplayPropertyComponent } from './components/main-content/display-property/display-property.component';
import { IntroComponent } from './components/main-content/intro/intro.component';
import { EnvironmentSelectorComponent } from './components/side-menu/environment-selector/environment-selector.component';

registerLocaleData(locale, 'da-DK');

const ngZorroConfig: NzConfig = {
  message: { nzTop: 70 },
};

@NgModule({
  bootstrap: [AppComponent, MsalRedirectComponent],
  declarations: [
    AddOrEditResponsibleUserDrawerComponent,
    AdminButtonComponent,
    AppComponent,
    CharacteristicLongTextDialogComponent,
    CharacteristicsTableComponent,
    ColourBrickComponent,
    CommentListComponent,
    ConnectorComponent,
    ConstructionDetailsComponent,
    ConstructionLogComponent,
    ContainsPipe,
    ControlSpecificationComponent,
    CostDetailsComponent,
    CreateAdminDrawerComponent,
    CreateEditMaterialBomDrawer,
    CreateQuickUrlDrawerComponent,
    DatasetCardListComponent,
    DesignDataComponent,
    DesignLookupComponent,
    DesignLookupSearchFieldComponent,
    DevelopmentMilestonesFrontendTableComponent,
    DevelopmentNoveltyTableComponent,
    DevelopmentNoveltyTableEditDrawer,
    DisplayPropertyComponent,
    EcoTableComponent,
    EditQuickUrlDrawerComponent,
    EDRevisionStructureComponent,
    ElementHistoryDocumentLinkComponent,
    ElementHistoryLogComponent,
    ElementLogComponent,
    ElementRiskAssessmentComponent,
    ElementStatusComponent,
    EnvironmentSelectorComponent,
    EquipmentComponent,
    EquipmentDetailsComponent,
    EquipmentSelectComponent,
    FooterComponent,
    GeneralInformationComponent,
    HeaderComponent,
    HistoryDetailsComponent,
    ImageSpinnerComponent,
    InfoCodesComponent,
    InfoCodesTableComponent,
    IntroComponent,
    ItemLogComponent,
    ItemLogsTableComponent,
    ItemRevisionDatasetTableComponent,
    ItemStructureComponent,
    LoginFailedComponent,
    MaintenanceManagementSystemDetailsComponent,
    ManageAdminsComponent,
    ManageQuickUrlsComponent,
    MaterialBomComponent,
    MaterialColourTableComponent,
    MaterialWhereUsedTableComponent,
    MouldCardDetailsComponent,
    MouldDecisionToolComponent,
    MouldDevelopersComponent,
    MouldLogComponent,
    MouldLogOverviewComponent,
    MouldLogOverviewModalComponent,
    MouldTechnicalSpecificationComponent,
    NotFoundComponent,
    OperationsTableComponent,
    PartDesignRevisionStructureComponent,
    PartDesignRevisionStructureComponent,
    PlanningReportComponent,
    ProcessingHistoryDocumentLinkComponent,
    ProfileDropdownComponent,
    RelatedSpecificationStructureComponent,
    RoleLinksPipe,
    RoleSelectionComponent,
    SharePointFileLinkCardComponent,
    SideMenuComponent,
    SiriusElementHistoryDocumentLinkComponent,
    SortPipe,
    TeamcenterComponent,
    TeamcenterTabHeaderComponent,
    TechnicalReviewComponent,
    TechnicalSpecificationComponent,
    ToySafetyReportComponent,
    TrimZeroesPipe,
    UnauthorizedComponent,
    UnitsOfMeasureComponent,
    ValidationComponent,
    ValidationItemComponent,
    ValidationSummaryModalComponent,
    VisibleForRolesDirective,
    VisitationComponent,
    WarningIconComponent,
    WorkcenterSelectorComponent,
    WorklistComponent,
    WorklistPageComponent,
  ],
  imports: [
    AppRoutingModule,
    ArrayIsNotEmptyPipe,
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    JoinDeepPipe,
    LegoUtilsModule,
    MouldLogApiModule.forRoot(
      () => new MouldLogApiConfiguration({ basePath: environment.mouldLogApi.url })
    ),
    MouldingProcessApiModule.forRoot(
      () => new MouldingProcessApiConfiguration({ basePath: environment.mouldingProcessApi.url })
    ),
    MouldDesignApiModule.forRoot(
      () => new MouldDesignApiConfiguration({ basePath: environment.mouldDesignApi.url })
    ),
    EquipmentApiModule.forRoot(
      () => new EquipmentApiConfiguration({ basePath: environment.equipmentApi.url })
    ),
    ToySafetyReportsApiModule.forRoot(
      () => new ToySafetyReportsApiConfiguration({ basePath: environment.toySafetyApi.url })
    ),
    MsalModule.forRoot(
      new PublicClientApplication(environment.msalConfig),
      environment.msalGuardConfig,
      environment.msalInterceptorConfig
    ),
    NzAffixModule,
    NzAlertModule,
    NzAvatarModule,
    NzBadgeModule,
    NzButtonModule,
    NzCardModule,
    NzCheckboxModule,
    NzCollapseModule,
    NzCommentModule,
    NzDatePickerModule,
    NzDescriptionsModule,
    NzDividerModule,
    NzDrawerModule,
    NzDropDownModule,
    NzEmptyModule,
    NzFormModule,
    NzGridModule,
    NzIconModule.forRoot(icons),
    NzInputModule,
    NzLayoutModule,
    NzListModule,
    NzModalModule,
    NzPageHeaderModule,
    NzPaginationModule,
    NzPopconfirmModule,
    NzRadioModule,
    NzResultModule,
    NzSelectModule,
    NzSkeletonModule,
    NzSpinModule,
    NzStatisticModule,
    NzSwitchModule,
    NzTableModule,
    NzTabsModule,
    NzTagModule,
    NzTimelineModule,
    NzTooltipModule,
    ReactiveFormsModule,
    SapApiModule.forRoot(),
  ],
  providers: [
    // Used for standard Angular pipes.
    { provide: LOCALE_ID, useValue: 'da-DK' },
    // Used for text and stuff in ng-zorro.
    { provide: NZ_I18N, useValue: en_US },
    // ng-zorro uses date-fns for date magic, and this is used for the locale.
    { provide: NZ_DATE_LOCALE, useValue: da },
    // https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/lib/msal-angular#3-get-tokens-for-web-api-calls
    {
      multi: true,
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
    },
    DecimalPipe,
    MsalBroadcastService,
    MsalGuard,
    MsalService,
    { provide: NZ_CONFIG, useValue: ngZorroConfig },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      // This is from the Sentry docs so just trust they know what they're talking about.
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
